import { and, eq } from "drizzle-orm";
import type { SQL } from "drizzle-orm";
import type { SQLiteColumn, SQLiteTable } from "drizzle-orm/sqlite-core";
import type { PowerSyncDrizzleDB } from "@malla/powersync";
import { nowISO } from "../utils/date-helpers";

/**
 * ── Local-first writes that CANNOT be lost silently ───────────────
 *
 * A Drizzle `UPDATE … WHERE id = ?` against the PowerSync **view** can
 * match **ZERO rows and return "success"**. This is not a hypothesis: it is measured in
 * `space-academy-app` (same canon, `activity` table of the local stack) and ported
 * to `listto-app` (`9ae347b6`) — this module is that same piece, adapted to the
 * POS domain of this repo (orders, catalog, cashbox, reservations, purchasing).
 *
 * The mechanism: when the CRUD queue is drained, PowerSync **rebuilds the
 * local view from the latest checkpoint**. If the checkpoint does not yet bring the
 * newly created row, the row is **ABSENT during that instant** — and an
 * UPDATE against it writes nothing, generates no op, and **does not fail**. In this
 * repo the most expensive case is precisely the one motivating the campaign: a product
 * or category is created and renamed on the fly (`updateProductMutation`,
 * `updateCategoryMutation`), or a newly created business rule (ingredient,
 * supplier, table, reservation) is adjusted seconds after its creation.
 *
 * Why the absence of an error proves absolutely nothing here: the server's
 * write-path **always returns 2xx on purpose** (a 4xx would block the client's queue
 * forever). A user write disappearing without a trace is the worst possible failure
 * mode: nothing flags it.
 *
 * Contract of this module: write, **RE-READ**, compare. If the value did not
 * land, retry with backoff; if still not landed after exhausting retries,
 * **THROW** — the caller communicates it instead of leaving the user looking at a
 * field that represents nothing saved.
 *
 * ── Adaptation to this repo: `extraWhere` ────────────────────────────────────
 * Several mutations (CC-12) pass along `organization_id`/`branch_id` as a
 * safety belt in addition to `id` in the UPDATE's `WHERE` (e.g.
 * `recomputeOrderTotals`, `applyOrderDiscountMutation`,
 * `closeRegisterSessionMutation`). `extraWhere` composes that extra predicate
 * ONLY on the write — the verification re-read remains by `id`,
 * which is the only thing that unambiguously identifies whether the row landed.
 *
 * ── What this module does NOT cover (deliberate) ────────────────────────────
 * Hard `DELETE`s (`purgeTrashItemMutation`, `deleteRecipeItemMutation`,
 * `deleteReservationMutation`) are not verified: their natural check would be
 * "the row is no longer there", and "absent" is precisely the ambiguous state of this window
 * — one cannot distinguish "it was deleted" from "it had not yet arrived".
 *
 * ── `user` IS covered (audit 2026-08-13, not an exception) ─────────────────
 * Why, and why it remains true after the `ThemeProvider` seed guard
 * (`uid !== VISITOR_ID`) that closed the root cause aggravating this same window:
 * see the docblock at the beginning of `packages/shared/src/users/mutations.ts`.
 * It is not repeated here to avoid two explanations that age separately.
 */

/**
 * Retry backoff. Total ≈ 6.05 s: more than covers the round trip of a
 * checkpoint without leaving a quick interaction —charging, canceling a line—
 * hanging forever if the row truly does not exist.
 */
export const VERIFIED_WRITE_BACKOFF_MS = [50, 100, 200, 400, 800, 1500, 3000] as const;

const sleep = (ms: number): Promise<void> =>
  new Promise((resolve) => {
    setTimeout(resolve, ms);
  });

export interface VerifiedWriteOptions {
  /** Waits between retries. Exposed so tests do not take seconds. */
  backoffMs?: readonly number[];
  /** Additional predicate composed with `eq(idColumn, id)` ONLY in the UPDATE. */
  extraWhere?: SQL;
}

/** Error of a local-first write that did not land in the view. */
export class LocalWriteLostError extends Error {
  constructor(
    readonly rowId: string,
    readonly fields: readonly string[],
  ) {
    super(
      `La escritura local no aterrizó en la fila ${rowId} (campos: ${fields.join(", ")}). ` +
        `La fila no está en la vista de PowerSync o el valor no se guardó.`,
    );
    this.name = "LocalWriteLostError";
  }
}

/** Reads the row by id, or `undefined` if it is not in the local view. */
async function readRow(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
): Promise<Record<string, unknown> | undefined> {
  // Drizzle cannot infer the shape of a generic `SQLiteTable`; the read
  // is by primary key, so the row is a flat record of columns.
  // oxlint-disable-next-line typescript/no-unsafe-type-assertion -- see above
  const rows = (await db
    .select()
    .from(table)
    .where(eq(idColumn, id))
    .limit(1)) as unknown as Record<string, unknown>[];
  return rows[0];
}

/** `null` and `undefined` are the same "empty" when compared to SQLite. */
const sameValue = (a: unknown, b: unknown): boolean => (a ?? null) === (b ?? null);

/**
 * Verified `UPDATE … WHERE id = ?`: writes, re-reads, compares the fields
 * of the patch and retries while they have not landed. Throws `LocalWriteLostError`
 * if they are still missing when backoff is exhausted.
 *
 * `updated_at` is set by this function on each attempt (and is not compared: it changes
 * on each pass); the `patch` must carry only domain fields.
 */
export async function patchRowVerified(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  patch: Record<string, unknown>,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;
  const whereClause = options.extraWhere
    ? and(eq(idColumn, id), options.extraWhere)
    : eq(idColumn, id);

  for (let attempt = 0; ; attempt++) {
    await db
      .update(table)
      .set({ ...patch, updated_at: nowISO() })
      .where(whereClause);

    const row = await readRow(db, table, idColumn, id);
    if (row && Object.entries(patch).every(([key, value]) => sameValue(row[key], value))) {
      return;
    }

    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, Object.keys(patch));
    }
    await sleep(wait);
  }
}

/**
 * Waits for the row to EXIST in the local view before touching it.
 *
 * For mutations that are not a simple patch, within an operation that
 * cannot be retried after the fact (e.g. a transaction that already generated a
 * sibling INSERT). Retrying the isolated UPDATE does not work there: the damage of operating
 * on an absent row is worse than losing a field — the UPDATE matches nothing
 * but the sibling INSERT does occur, leaving duplicated or orphaned state.
 */
export async function waitForRowPresent(
  db: PowerSyncDrizzleDB,
  table: SQLiteTable,
  idColumn: SQLiteColumn,
  id: string,
  options: VerifiedWriteOptions = {},
): Promise<void> {
  const backoffMs = options.backoffMs ?? VERIFIED_WRITE_BACKOFF_MS;

  for (let attempt = 0; ; attempt++) {
    if (await readRow(db, table, idColumn, id)) {
      return;
    }
    const wait = backoffMs[attempt];
    if (wait === undefined) {
      throw new LocalWriteLostError(id, ["<fila ausente>"]);
    }
    await sleep(wait);
  }
}
